import React from "react";
import { InboxRow } from "component-library";

import { AlertType, IClientAlert } from "models/clientAlert";

import withPagination, { IWithPaginationProps } from "hoc/withPagination";

import { IClientAlertsPageTexts } from "pages/JobsPage/ClientAlertsPage/ClientAlertsPage";
import {
  IPrepareAdditionalInfoData,
  prepareAdditionalInfo,
} from "pages/JobsPage/shared/shared.helpers";

import SelectAllControl from "components/SelectAllControl/SelectAllControl";

import { getLabel, getTitle } from "./ClientAlertsView.helpers";

import "./ClientAlertsView.scss";

export interface IClientAlertsViewProps extends IWithPaginationProps {
  texts: IClientAlertsPageTexts;
  items: IClientAlert[];
  selectedItems: string[];
  deselectItems: (clientAlertId: string[]) => void;
  selectItems: (clientAlertId: string[]) => void;
  onMarkAsRead: (clientAlertId: string[]) => void;
  onMarkAsUnread: (clientAlertId: string[]) => void;
  onClientAlertClick: (item: IClientAlert) => void;
  showTopControls?: boolean;
}

export class ClientAlertsView extends React.PureComponent<IClientAlertsViewProps> {
  public render() {
    const { items, showTopControls = true } = this.props;

    return (
      <section className="c-client-alerts-view">
        {showTopControls && (
          <div>
            {this.renderTopBar()}
            {this.renderSelectAllControl()}
          </div>
        )}
        <table className="c-client-alerts-view__body">
          {items.map((item: IClientAlert) => this.renderListItem(item))}
        </table>
        {this.renderBottomBar()}
      </section>
    );
  }

  private renderListItem = (item: IClientAlert) => {
    const { texts, selectedItems } = this.props;
    const isSelected = selectedItems.includes(item.id);
    const sender = `${item.performer.forename} ${item.performer.surname}`;
    const additionalInfo: IPrepareAdditionalInfoData = {
      sentDate: item.dateCreated,
      closingDate: item.breakdown.closingDate,
      closedEarly: item.breakdown.closedEarly,
      closedReason: item.breakdown.closedReason,
    };
    const titleText =
      item.alertType === AlertType.Nudge
        ? texts.clientAlertTitleNudge
        : texts.clientAlertTitleSuggestion;

    return (
      <InboxRow
        key={item.id}
        sender={sender}
        isChecked={isSelected}
        value={item.id}
        title={getTitle(item, titleText)}
        additionalInfo={prepareAdditionalInfo(additionalInfo, texts)}
        isRead={item.read}
        labels={[getLabel(item.alertType, texts)]}
        onClick={this.handleClick(item)}
        onSelect={this.handleSelect(isSelected, item.id)}
      />
    );
  };

  public renderTopBar() {
    const { renderPaginationResultStats, renderPagination } = this.props;

    return (
      <div className="c-client-alerts-view__top-bar">
        <div className="c-client-alerts-view__count-info">
          {renderPaginationResultStats()}
        </div>
        <div className="c-client-alerts-view__pagination">
          {renderPagination()}
        </div>
      </div>
    );
  }

  public renderSelectAllControl() {
    return (
      <SelectAllControl
        selectItems={this.props.selectItems}
        deselectItems={this.props.deselectItems}
        selectedItems={this.props.selectedItems}
        markItemsAsRead={this.markItemsAsRead}
        markItemsAsUnread={this.markItemsAsUnread}
        items={this.props.items}
      />
    );
  }

  public renderBottomBar() {
    const { renderPagination } = this.props;

    return (
      <div className="c-client-alerts-view__bottom-bar">
        <div className="c-client-alerts-view__pagination">
          {renderPagination(true)}
        </div>
      </div>
    );
  }

  private handleSelect = (isSelected: boolean, id: string) => () => {
    if (isSelected) {
      return this.props.deselectItems([id]);
    }

    return this.props.selectItems([id]);
  };

  private handleClick = (item: IClientAlert) => () => {
    this.props.onClientAlertClick(item);
  };

  private markItemsAsRead = (clientAlertId: string[]) => {
    this.props.onMarkAsRead(clientAlertId);
  };

  private markItemsAsUnread = (clientAlertId: string[]) => {
    this.props.onMarkAsUnread(clientAlertId);
  };
}

export default withPagination<IClientAlertsViewProps>(ClientAlertsView);
